import { useState } from "react";
import { ChevronLeft, ChevronRight, GraduationCap } from "lucide-react";
import { CourseCard } from "./CourseCard";
import { CourseOverviewDialog } from "./CourseOverviewDialog";
import { cn } from "../../app/BusinessDash/lib/utils";

// Main courses
const mainCourses = [
  {
    id: "sustainable-business-understanding",
    title: "Sustainable Business Understanding",
    shortDescription: "Gain a fundamental understanding of sustainability in a business context and learn to integrate it into your organization.",
    fullDescription: "This course provides you with a solid foundational understanding of sustainability from a business perspective. You will learn to understand the key concepts, frameworks, and drivers behind sustainable business, and how to apply this knowledge in your own organization.",
    duration: "4 weeks",
    format: "Hybrid",
    ects: 5,
    topics: [
      "Fundamental sustainability concepts",
      "Business drivers for sustainability",
      "Triple bottom line",
      "Stakeholder analysis",
      "Sustainable value creation",
      "Case-based learning",
    ],
  },
  {
    id: "green-transition-in-practice",
    title: "Green Transition in Practice",
    shortDescription: "Learn to implement green transition in your organization with practical tools and methods.",
    fullDescription: "This course focuses on the practical implementation of green transition. You will gain hands-on experience with tools and methods to drive change in your organization and create measurable results.",
    duration: "8 weeks",
    format: "Hybrid",
    ects: 10,
    topics: [
      "Change management for green transition",
      "Practical implementation tools",
      "Measurement and documentation",
      "Employee engagement",
      "Barriers and solutions",
      "Best practices from other companies",
    ],
    isComingSoon: true,
  },
  {
    id: "carbon-accounting",
    title: "Carbon Accounting",
    shortDescription: "Become an expert in carbon accounting, the GHG protocol, and calculating scope 1, 2, and 3 emissions.",
    fullDescription: "Learn to prepare carbon accounts according to the GHG protocol. The course covers scope 1, 2, and 3 emissions, data collection, emission factors, and how to translate accounting figures into concrete actions in your organization.",
    duration: "4 weeks",
    format: "Online",
    ects: 5,
    topics: [
      "GHG protocol and standards",
      "Scope 1, 2, and 3 emissions",
      "Data collection and quality assurance",
      "Emission factors and calculations",
      "From accounting to action",
      "Verification and reporting",
    ],
  },
  {
    id: "final-project",
    title: "Final Project",
    shortDescription: "Complete your education with a practical project where you apply all your learning in your own organization.",
    fullDescription: "The final project is the concluding course of the entire program. Here you have the opportunity to work on a concrete sustainability project in your own organization and demonstrate your combined learning throughout the education.",
    duration: "8 weeks",
    format: "Hybrid",
    ects: 10,
    topics: [
      "Project planning and management",
      "Practical application of theory",
      "Stakeholder management",
      "Documentation and reporting",
      "Presentation and communication",
      "Feedback and reflection",
    ],
  },
];

// Elective courses
const electiveCourses = [
  {
    id: "sustainability-behavior-design",
    title: "Sustainability and Behavior Design",
    shortDescription: "Learn to apply behavior design to promote sustainable choices and actions in organizations.",
    fullDescription: "This course combines sustainability with behavioral psychology. You will learn to design interventions that motivate employees and customers to make more sustainable choices.",
    duration: "4 weeks",
    format: "Online",
    ects: 5,
    topics: [
      "Behavioral psychology and nudging",
      "Designing sustainable choices",
      "Employee motivation",
      "Customer behavior",
      "Measuring behavioral change",
      "Ethical considerations",
    ],
    isElective: true,
    isComingSoon: true,
  },
  {
    id: "sustainable-digital-transformation",
    title: "Sustainable Digital Transformation",
    shortDescription: "Combine digital transformation with sustainability to create a future-proof organization.",
    fullDescription: "Explore how digitalization can accelerate your organization's sustainability journey. The course covers green IT strategies, data analysis for sustainability measurement, and how technology can support circular economy.",
    duration: "4 weeks",
    format: "Online",
    ects: 5,
    topics: [
      "Green IT and cloud computing",
      "Data for sustainability reporting",
      "Digital tools for lifecycle analysis",
      "Automation of compliance",
      "IoT and resource tracking",
      "Sustainable software development",
    ],
    isElective: true,
    isComingSoon: true,
  },
  {
    id: "corporate-sustainability-strategy",
    title: "Corporate Sustainability Strategy",
    shortDescription: "Develop and implement a sustainable strategy that creates value for both business and society.",
    fullDescription: "This course provides you with tools to develop an ambitious and realistic sustainability strategy. You will learn to set goals, prioritize efforts, and integrate sustainability into the company's overall strategy.",
    duration: "8 weeks",
    format: "Hybrid",
    ects: 10,
    topics: [
      "Strategic analysis and planning",
      "Goal setting and KPIs",
      "Integration with business strategy",
      "Stakeholder management",
      "Implementation and follow-up",
      "Strategy communication",
    ],
    isElective: true,
    isComingSoon: true,
  },
  {
    id: "esg-reporting",
    title: "ESG Reporting",
    shortDescription: "Master ESG reporting and learn to communicate your organization's sustainability efforts effectively.",
    fullDescription: "This course gives you an in-depth understanding of ESG reporting, including CSRD, EU taxonomy, and other relevant standards. You will learn to collect data, structure reports, and communicate effectively to stakeholders.",
    duration: "4 weeks",
    format: "Hybrid",
    ects: 5,
    topics: [
      "CSRD and EU taxonomy",
      "ESG data management",
      "Reporting standards (GRI, SASB)",
      "Double materiality analysis",
      "Stakeholder communication",
      "Compliance and audit",
    ],
    isElective: true,
  },
];

const allCourses = [...mainCourses, ...electiveCourses];

export const CourseBrowser = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(allCourses.length - 1, prev + 1));
  };

  const handleCourseClick = () => {
    setOverviewDialogOpen(true);
  };

  const currentCourse = allCourses[currentIndex];

  return (
    <div 
      className="space-y-2"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ fontSize: "0.875rem" }}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <GraduationCap className="h-4 w-4 text-tier-education" />
        <span className="text-xs font-medium text-foreground">Browse Courses</span>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {currentIndex + 1} / {allCourses.length}
        </span>
      </div>

      {/* Carousel with arrows on sides */}
      <div className="flex items-center gap-1">
        {/* Left arrow */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
          disabled={currentIndex === 0}
          className={cn(
            "p-2 rounded-lg transition-colors shrink-0",
            currentIndex === 0
              ? "text-muted-foreground/30 cursor-not-allowed"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {/* Single course card */}
        <div className="flex-1">
          <CourseCard
            course={currentCourse}
            isSelected={false}
            onClick={handleCourseClick}
          />
        </div>

        {/* Right arrow */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          disabled={currentIndex >= allCourses.length - 1}
          className={cn(
            "p-1 rounded transition-colors shrink-0",
            currentIndex >= allCourses.length - 1
              ? "text-muted-foreground/30 cursor-not-allowed"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          )}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Course overview dialog */}
      <CourseOverviewDialog
        courses={allCourses}
        open={overviewDialogOpen}
        onOpenChange={setOverviewDialogOpen}
      />
    </div>
  );
};

